/**
 * Endpoints para gestión de recetas y sistema nutricional.
 * Base URL: /api/recipes/
 */
import { GET, POST, PATCH, DELETE } from "../config";

// ==================== RECETAS ====================

/**
 * Listar recetas
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (branch_id, status, recipe_type, search)
 * @returns {Promise} Lista de recetas
 */
export const list = async (page = 1, filters = {}) => {
  let url = `recipes/recipes/?page=${page}`;
  url += `&page_size=50`;

  if (filters.branch_id) {
    url += `&branch_id=${filters.branch_id}`;
  }

  if (filters.status) {
    url += `&status=${filters.status}`;
  }

  if (filters.recipe_type) {
    url += `&recipe_type=${filters.recipe_type}`;
  }

  if (filters.search) {
    url += `&search=${encodeURIComponent(filters.search)}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener una receta por ID
 * @param {string} id - UUID de la receta
 * @returns {Promise} Datos de la receta
 */
export const retrieve = async (id) => {
  const response = await GET(`recipes/recipes/${id}/`);
  return response.data;
};

/**
 * Crear una nueva receta
 * @param {object} data - Datos de la receta
 * @returns {Promise} Receta creada
 */
export const create = async (data) => {
  const response = await POST("recipes/recipes/", data);
  return response.data;
};

/**
 * Actualizar una receta
 * @param {string} id - UUID de la receta
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Receta actualizada
 */
export const update = async (id, data) => {
  const response = await PATCH(`recipes/recipes/${id}/`, data);
  return response.data;
};

/**
 * Eliminar una receta
 * @param {string} id - UUID de la receta
 * @returns {Promise}
 */
export const destroy = async (id) => {
  const response = await DELETE(`recipes/recipes/${id}/`);
  return response.data;
};

/**
 * Dashboard de recetas con estadísticas básicas
 * @returns {Promise} Estadísticas del dashboard
 */
export const dashboard = async () => {
  const response = await GET("recipes/recipes/dashboard/");
  return response.data;
};

/**
 * Dashboard avanzado con estadísticas de rendimiento, nutrición y administración
 * @returns {Promise} Estadísticas avanzadas
 */
export const statsDashboard = async () => {
  const response = await GET("recipes/recipes/stats_dashboard/");
  return response.data;
};

// ==================== NUTRICIÓN ====================

/**
 * Calcular valores nutricionales de una receta
 * @param {string} id - UUID de la receta
 * @returns {Promise} Datos nutricionales calculados
 */
export const calculateNutrition = async (id) => {
  const response = await POST(`recipes/recipes/${id}/calculate_nutrition/`);
  return response.data;
};

/**
 * Actualizar y recalcular valores nutricionales
 * @param {string} id - UUID de la receta
 * @param {object} data - Datos (total_yield_grams)
 * @returns {Promise} Datos nutricionales actualizados
 */
export const updateNutrition = async (id, data) => {
  const response = await POST(`recipes/recipes/${id}/update_nutrition/`, data);
  return response.data;
};

/**
 * Obtener etiqueta nutricional formateada (MINSAL)
 * @param {string} id - UUID de la receta
 * @returns {Promise} Datos de etiqueta nutricional
 */
export const getNutritionLabel = async (id) => {
  const response = await GET(`recipes/recipes/${id}/nutrition_label/`);
  return response.data;
};

/**
 * Descargar PDF de etiqueta nutricional (62mm x 100mm vertical)
 * @param {string} id - UUID de la receta
 * @returns {Promise} Blob del PDF
 */
export const downloadNutritionLabelPDF = async (id) => {
  // Usar fetch directamente para manejar blob
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const { DEVURL } = await import('../config');
  const baseURL = DEVURL || 'http://localhost:8000/api';
  const url = `${baseURL}/recipes/recipes/${id}/download-nutrition-label-pdf/`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Token ${token}` : '',
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error descargando PDF' }));
    throw new Error(error.error || 'Error descargando PDF');
  }
  
  return await response.blob();
};

/**
 * Listar ingredientes nutricionales disponibles
 * @param {string} branchId - ID de la sucursal (opcional)
 * @returns {Promise} Lista de ingredientes nutricionales
 */
export const getNutritionalIngredients = async (branchId = null) => {
  let url = "recipes/recipes/nutritional_ingredients/";
  if (branchId) {
    url += `?branch_id=${branchId}`;
  }
  const response = await GET(url);
  return response.data;
};

// ==================== COSTOS ====================

/**
 * Calcular costo total de la receta
 * @param {string} id - UUID de la receta
 * @param {object} options - Opciones (include_overhead, include_labor)
 * @returns {Promise} Datos de costo
 */
export const calculateCost = async (id, options = {}) => {
  let url = `recipes/recipes/${id}/calculate_cost/`;
  const params = new URLSearchParams();
  if (options.include_overhead !== undefined) {
    params.append("include_overhead", options.include_overhead);
  }
  if (options.include_labor !== undefined) {
    params.append("include_labor", options.include_labor);
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  const response = await GET(url);
  return response.data;
};

/**
 * Actualizar costos de la receta
 * @param {string} id - UUID de la receta
 * @returns {Promise} Datos de costo actualizados
 */
export const updateCosts = async (id) => {
  const response = await POST(`recipes/recipes/${id}/update_costs/`);
  return response.data;
};

/**
 * Análisis de rentabilidad
 * @param {string} id - UUID de la receta
 * @param {number} targetPrice - Precio objetivo (opcional)
 * @returns {Promise} Análisis de rentabilidad
 */
export const profitabilityAnalysis = async (id, targetPrice = null) => {
  let url = `recipes/recipes/${id}/profitability_analysis/`;
  if (targetPrice) {
    url += `?target_price=${targetPrice}`;
  }
  const response = await GET(url);
  return response.data;
};

/**
 * Comparar costos entre recetas
 * @param {object} filters - Filtros (recipe_type)
 * @returns {Promise} Comparación de costos
 */
export const costComparison = async (filters = {}) => {
  let url = "recipes/recipes/cost_comparison/";
  const params = new URLSearchParams();
  if (filters.recipe_type) {
    params.append("recipe_type", filters.recipe_type);
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  const response = await GET(url);
  return response.data;
};

// ==================== ESCALADO ====================

/**
 * Escalar receta por un factor
 * @param {string} id - UUID de la receta
 * @param {number} scaleFactor - Factor de escalado
 * @returns {Promise} Receta escalada
 */
export const scale = async (id, scaleFactor) => {
  const response = await POST(`recipes/recipes/${id}/scale/`, {
    scale_factor: scaleFactor,
  });
  return response.data;
};

// ==================== INGREDIENTES DE RECETAS ====================

/**
 * Listar ingredientes de una receta
 * @param {string} recipeId - UUID de la receta
 * @returns {Promise} Lista de ingredientes
 */
export const listIngredients = async (recipeId) => {
  const response = await GET(`recipes/ingredients/?recipe=${recipeId}&page_size=1000`);
  return response.data;
};

/**
 * Crear un ingrediente para una receta
 * @param {object} data - Datos del ingrediente (recipe, ingredient, quantity, unit)
 * @returns {Promise} Ingrediente creado
 */
export const createIngredient = async (data) => {
  const response = await POST("recipes/ingredients/", data);
  return response.data;
};

/**
 * Actualizar un ingrediente de receta
 * @param {number} id - ID del ingrediente
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Ingrediente actualizado
 */
export const updateIngredient = async (id, data) => {
  const response = await PATCH(`recipes/ingredients/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un ingrediente de receta
 * @param {number} id - ID del ingrediente
 * @returns {Promise}
 */
export const deleteIngredient = async (id) => {
  const response = await DELETE(`recipes/ingredients/${id}/`);
  return response.data;
};
